import { err, ok, Result } from 'neverthrow';
import { Graph } from '../graph';
import { CommitsBuildFailure } from './types';
import { Assert } from '@cotera/utilities';

type IdentEditCallback<NodeTy extends Graph.GraphNode> = (
  node: Omit<NodeTy, 'meta'>
) => Result<Omit<NodeTy, 'meta'>, CommitsBuildFailure>;

export class IdentityMap {
  private constructor(
    private readonly map: { readonly [identString: string]: Graph.GraphNode }
  ) {}

  length(): number {
    return Object.keys(this.map).length;
  }

  static new(): IdentityMap {
    return new this({});
  }

  /**
   * Create an IdentityMap from a raw map object.
   * Used for deserialization from worker threads.
   */
  static fromMap(map: {
    readonly [identString: string]: Graph.GraphNode;
  }): IdentityMap {
    return new this(map);
  }

  /**
   * Get the raw map for serialization to worker threads.
   */
  get rawMap(): { readonly [identString: string]: Graph.GraphNode } {
    return this.map;
  }

  entries(): [Graph.Identity, Graph.GraphNode][] {
    return Object.entries(this.map).map(([key, node]) => [
      Graph.Identity.fromString(key),
      node,
    ]);
  }

  get(identity: Graph.Identity): Graph.GraphNode | null {
    return this.map[identity.toString()] ?? null;
  }

  add(identity: Graph.Identity, node: Graph.GraphNode): IdentityMap {
    return this.newWith(identity, node);
  }

  setLockStatus(
    identity: Graph.Identity,
    at: Graph.Identity,
    isLocked: boolean
  ) {
    const { meta, ...node } = Assert.assertExists(this.get(identity));

    return this.newWith(identity, {
      ...node,
      meta: { ...meta, editsAt: [...meta.editsAt, at], isLocked },
    });
  }

  addEdit(identity: Graph.Identity, at: Graph.Identity) {
    const { meta, ...node } = Assert.assertExists(this.get(identity));

    return this.newWith(identity, {
      ...node,
      meta: { ...meta, editsAt: [...meta.editsAt, at] },
    });
  }

  editRel(
    identity: Graph.Identity,
    at: Graph.Identity,
    cb: IdentEditCallback<Graph.RelNode>
  ): Result<IdentityMap, CommitsBuildFailure> {
    return this.edit(identity, at, (node) => {
      if (node.t === 'rel') {
        return cb(node as Omit<Graph.RelNode, 'meta'>);
      }

      const e: CommitsBuildFailure = {
        t: 'cant-update-different-kind',
        curr: node.t,
        attempted: 'rel',
      };

      return err(e);
    });
  }

  editExpr(
    identity: Graph.Identity,
    at: Graph.Identity,
    cb: IdentEditCallback<Graph.ExprNode>
  ): Result<IdentityMap, CommitsBuildFailure> {
    return this.edit(identity, at, (node) => {
      if (node.t === 'expr') {
        return cb(node as Omit<Graph.ExprNode, 'meta'>);
      }

      const e: CommitsBuildFailure = {
        t: 'cant-update-different-kind',
        curr: node.t,
        attempted: 'rel',
      };

      return err(e);
    });
  }

  editDocument(
    identity: Graph.Identity,
    at: Graph.Identity,
    cb: IdentEditCallback<Graph.DocumentNode>
  ): Result<IdentityMap, CommitsBuildFailure> {
    return this.edit(identity, at, (node) => {
      if (node.t === 'document') {
        return cb(node as Omit<Graph.DocumentNode, 'meta'>);
      }

      const e: CommitsBuildFailure = {
        t: 'cant-update-different-kind',
        curr: node.t,
        attempted: 'document',
      };

      return err(e);
    });
  }

  editAgent(
    identity: Graph.Identity,
    at: Graph.Identity,
    cb: IdentEditCallback<Graph.AgentNode>
  ): Result<IdentityMap, CommitsBuildFailure> {
    return this.edit(identity, at, (node) => {
      if (node.t === 'agent') {
        return cb(node as Omit<Graph.AgentNode, 'meta'>);
      }

      const e: CommitsBuildFailure = {
        t: 'cant-update-different-kind',
        curr: node.t,
        attempted: 'agent',
      };

      return err(e);
    });
  }

  editTool(
    identity: Graph.Identity,
    at: Graph.Identity,
    cb: IdentEditCallback<Graph.ToolNode>
  ): Result<IdentityMap, CommitsBuildFailure> {
    return this.edit(identity, at, (node) => {
      if (node.t === 'tool') {
        return cb(node as Omit<Graph.ToolNode, 'meta'>);
      }

      const e: CommitsBuildFailure = {
        t: 'cant-update-different-kind',
        curr: node.t,
        attempted: 'tool',
      };

      return err(e);
    });
  }

  edit(
    identity: Graph.Identity,
    at: Graph.Identity,
    cb: IdentEditCallback<Graph.GraphNode>
  ): Result<IdentityMap, CommitsBuildFailure> {
    const { meta, ...node } = Assert.assertExists(this.get(identity));

    if (meta.isLocked) {
      return err({ t: 'ident-locked', identity });
    }

    const res = cb(node);

    if (res.isErr()) {
      return err(res.error);
    }

    const newMeta: Graph.NodeMeta = { ...meta, editsAt: [...meta.editsAt, at] };
    // distributing `Omit` across a union makes this weird
    const newNode = {
      ...res.value,
      meta: newMeta,
    } as Graph.GraphNode;

    return ok(this.newWith(identity, newNode));
  }

  private newWith(
    identity: Graph.Identity,
    node: Graph.GraphNode
  ): IdentityMap {
    return new IdentityMap({
      ...this.map,
      [identity.toString()]: node,
    });
  }
}
